import axios from "axios";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CREATE_jwel } from "../../../Config/Config";
import dayjs from "dayjs";

const INVOICE_API =
  "https://retailtextiles.timeserasoftware.in/api/ERP/GetDataFromGivenTableNamewithwhere";
const INVOICE_TABLE = "CREDIT_BILLMAST"; // change if your bills live in another table

const lowerKeys = (obj = {}) =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v]));
const getPath = (x, y, width, height) =>
  `M${x},${y + height} C${x + width / 3},${y + height} ${x + width / 2},${y + height / 3} ${x + width / 2},${y}
   C${x + width / 2},${y + height / 3} ${x + (2 * width) / 3},${y + height} ${x + width},${y + height} Z`;

const TriangleBar = ({ fill, x, y, width, height }) => (
  <path d={getPath(x, y, width, height)} stroke="none" fill={fill} />
);

// One distinct hue per day (not shades of the same purple) — same vivid
// palette used elsewhere in the app for multi-item bullets/dots, so it
// stays visually consistent across screens.
const TREND_COLORS = [
  "#f43f5e",
  "#f59e0b",
  "#10b981",
  "#3b82f6",
  "#a855f7",
  "#ec4899",
  "#14b8a6",
];

// Custom value-label renderer so each bar's "₹Xk" label picks up that
// same bar's color instead of one flat purple for every label.
const ColoredValueLabel = ({ x, y, width, value, index }) => {
  if (!value) return null;
  const color = TREND_COLORS[index % TREND_COLORS.length];
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      textAnchor="middle"
      fontSize={9}
      fontWeight={700}
      fill={color}
    >
      {`₹${(value / 1000).toFixed(0)}k`}
    </text>
  );
};

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "rgba(44,27,105,0.93)",
        borderRadius: 10,
        padding: "8px 12px",
        color: "#fff",
        fontSize: 11,
        boxShadow: "0 4px 16px rgba(44,27,105,0.3)",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 3 }}>{label}</div>
      <div>
        Sales:{" "}
        <strong>
          ₹
          {Number(payload[0]?.value || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
          })}
        </strong>
      </div>
    </div>
  );
};

const TodaysSalesBarGraph = () => {
  const [trendData, setTrendData] = useState([]);
  const tenantName = localStorage.getItem("tenantName");

  // const fetchDayAmt = async (dateObj) => {
  //   try {
  //     const formatted = dateObj.toLocaleDateString("en-US", {
  //       year: "numeric",
  //       month: "2-digit",
  //       day: "2-digit",
  //     });
  //     const res = await axios.get(
  //       `${CREATE_jwel}/api/Master/GetDataFromGivenTableNameWithWhere?tableName=BILL_MAST&where=BILLDATE>='${formatted}' AND BILLDATE<='${formatted}'`,
  //       { headers: { tenantName } },
  //     );
  //     const data = res.data;
  //     return Array.isArray(data) && data.length > 0
  //       ? data.reduce((sum, item) => sum + (Number(item.NetAmt) || 0), 0)
  //       : 0;
  //   } catch {
  //     return 0;
  //   }
  // };

  useEffect(() => {
    const load = async () => {
      try {
        const end = dayjs();
        const start = end.subtract(6, "day");

        const res = await axios.get(
          `${INVOICE_API}?tableName=${INVOICE_TABLE}&where=BILLDATE>='${start.format("MM/DD/YYYY")}' AND BILLDATE<='${end.format("MM/DD/YYYY")}'`,
          { headers: { tenantName } },
        );

        const rows = Array.isArray(res.data) ? res.data : [];
        const totals = {};
        rows.forEach((row) => {
          const n = lowerKeys(row);
          if (String(n.cancell || "").toUpperCase() === "YES") return; // skip cancelled bills
          const key = dayjs(n.billdate).format("YYYY-MM-DD");
          totals[key] = (totals[key] || 0) + (Number(n.netamt) || 0);
        });

        setTrendData(
          [6, 5, 4, 3, 2, 1, 0].map((offset) => {
            const d = end.subtract(offset, "day");
            return {
              date: d.toDate().toLocaleDateString("en-IN", {
                month: "short",
                day: "2-digit",
              }),
              amt: totals[d.format("YYYY-MM-DD")] || 0,
            };
          }),
        );
      } catch {
        setTrendData([]);
      }
    };
    load();
  }, [tenantName]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      style={{
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 4px 20px rgba(44,27,105,0.10)",
        padding: "12px 12px 8px",
        height: 280,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ marginBottom: 8, flexShrink: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#2d1b69" }}>
          Sales — Last 7 Days
        </div>
        <div style={{ fontSize: 9, color: "#aaa", marginTop: 1 }}>
          Daily net sales trend
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height={210}>
          <BarChart
            data={trendData.length ? trendData : [{ date: "—", amt: 0 }]}
            margin={{ top: 18, right: 10, left: 0, bottom: 4 }}
            barSize={30}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f0eaff"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              interval={0}
              tick={{ fontSize: 10, fontWeight: 600, fill: "#4c3494" }}
              axisLine={false}
              tickLine={false}
              angle={-38}
              textAnchor="end"
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#aaa" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) =>
                v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
              }
            />
            <Tooltip
              content={<TrendTooltip />}
              cursor={{ fill: "rgba(184,164,232,0.12)" }}
            />
            <Bar
              dataKey="amt"
              shape={<TriangleBar />}
              animationDuration={900}
              label={<ColoredValueLabel />}
            >
              {(trendData.length ? trendData : [{}]).map((_, i) => (
                <Cell key={i} fill={TREND_COLORS[i % TREND_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
};

export default TodaysSalesBarGraph;